import * as actions from './Actions';

const parseQuiz = (payload) => {
  let creator = payload.creator ? {
    id: Number(payload.creator.id ?? payload.createdBy ?? 0),
    username: payload.creator.username || payload.creator.name || '',
    email: payload.creator.email || '',
  } : undefined;

  // Fallback immediately after create: backend may not populate `creator`
  if (!creator && (payload.createdBy !== undefined && payload.createdBy !== null)) {
    let currentUser = null;
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const raw = window.localStorage.getItem('user') || window.localStorage.getItem('authUser');
        currentUser = raw ? JSON.parse(raw) : null;
      }
    } catch (_) { /* ignore */ }
    const username = payload.createdByUsername || payload.createdByName || currentUser?.username || currentUser?.user?.username || currentUser?.name || '';
    const email = currentUser?.email || '';
    creator = { id: Number(payload.createdBy), username, email };
  }

  const relatedSkills = Array.isArray(payload.relatedSkills) ? payload.relatedSkills : [];
  const questionsCount = Number(
    payload.questionsCount ?? (Array.isArray(payload.questions) ? payload.questions.length : NaN)
  );
  const skillsCount = Number(
    payload.skillsCount ?? (Array.isArray(relatedSkills) ? relatedSkills.length : NaN)
  );

  // Normalize poste information
  const posteId = Number(
    payload.posteId ?? payload.poste_id ?? payload.posteId?.id ?? payload.poste?.posteId ?? payload.poste?.id
  );
  const poste = payload.poste
    ? {
        posteId: Number((payload.poste.posteId ?? payload.poste.id ?? posteId) ?? 0),
        name: payload.poste.name || payload.poste.title || '',
      }
    : (Number.isFinite(posteId) ? { posteId, name: payload.posteName || '' } : undefined);

  return {
    quizId: payload.quizId ?? payload.id,
    createdBy: Number(payload.createdBy ?? creator?.id ?? 0),
    creator, // object with {id, username, email}
    title: payload.title || '',
    description: payload.description || '',
    timeLimit: Number(payload.timeLimit ?? 0),
    maxAttempts: Number(payload.maxAttempts ?? 1),
    shuffleQuestions: Boolean(payload.shuffleQuestions ?? false),
    shuffleOptions: Boolean(payload.shuffleOptions ?? false),
    isActive: Boolean(payload.isActive ?? true),
    createdAt: payload.createdAt || new Date().toISOString(),
    updatedAt: payload.updatedAt || new Date().toISOString(),
    questionsCount: Number.isFinite(questionsCount) ? questionsCount : 0,
    skillsCount: Number.isFinite(skillsCount) ? skillsCount : 0,
    posteId: Number.isFinite(posteId) ? posteId : undefined,
    poste,
  };
};

export const quizzesReducer = (state = [], action) => {
  switch (action.type) {
    case actions.INITIALIZE_QUIZZES:
      return Array.isArray(action.payload) ? action.payload.map(parseQuiz) : [];

    case actions.ADD_QUIZ:
      return [...state, parseQuiz(action.payload)];

    case actions.UPDATE_QUIZ:
      return state.map(q => (q.quizId === (action.payload.quizId ?? action.payload.id) ? parseQuiz(action.payload) : q));

    case actions.DELETE_QUIZ: {
      const id = action.quizId ?? action.id;
      return state.filter(q => q.quizId !== id);
    }

    case actions.CLEAR_QUIZZES_LIST:
      return [];

    // Keep quiz card counters in sync with question create/delete actions
    case actions.ADD_QUIZ_QUESTION: {
      const qid = Number(action.payload?.quizId ?? action.quizId);
      if (!Number.isFinite(qid)) return state;
      return state.map((q) => {
        if (Number(q.quizId) !== qid) return q;
        const prevQuestions = Number.isFinite(Number(q.questionsCount)) ? Number(q.questionsCount) : 0;
        const nextQuestions = prevQuestions + 1;
        // Minimal heuristic for skillsCount: if this is the first question, ensure skillsCount >= 1
        const prevSkills = Number.isFinite(Number(q.skillsCount)) ? Number(q.skillsCount) : 0;
        const nextSkills = prevQuestions === 0 ? Math.max(prevSkills, 1) : prevSkills;
        return { ...q, questionsCount: nextQuestions, skillsCount: nextSkills, updatedAt: new Date().toISOString() };
      });
    }
    case actions.DELETE_QUIZ_QUESTION: {
      const qid = Number(action.payload?.quizId ?? action.quizId);
      if (!Number.isFinite(qid)) return state;
      return state.map((q) => {
        if (Number(q.quizId) !== qid) return q;
        const prevQuestions = Number.isFinite(Number(q.questionsCount)) ? Number(q.questionsCount) : 0;
        const nextQuestions = Math.max(0, prevQuestions - 1);
        // If no questions remain, reset skillsCount to 0
        const nextSkills = nextQuestions === 0 ? 0 : (Number.isFinite(Number(q.skillsCount)) ? Number(q.skillsCount) : 0);
        return { ...q, questionsCount: nextQuestions, skillsCount: nextSkills, updatedAt: new Date().toISOString() };
      });
    }

    default:
      return state;
  }
};

export default quizzesReducer;
